export type EquipamentStatus = "em_uso" | "disponivel" | "danificado";

export interface EquipamentHistory {
  id: number;
  equipamentId: number;
  /**
   * Colaborador que teve o equipamento. Null = período em que ele ficou disponível (ou posse
   * antiga cujo nome não deu para casar com ninguém no cadastro).
   *
   * Substituiu um `userId: string` que existia no modelo e **nunca** foi preenchido — o histórico
   * inteiro era nome livre, e nome não aguenta homônimo nem mudança de cadastro.
   */
  employeeId: number | null;
  /** Nome NA ÉPOCA do período — é o que mantém o histórico legível quando o cadastro muda. */
  userName: string | null;
  startDate: string;
  endDate: string | null;
  notes: string | null;
  equipament?: Equipament;
}

/**
 * Uma instalação do widget num perfil do Windows daquela máquina.
 *
 * A lista de agentes de um equipamento é, por construção, **quem já usou aquele notebook** — sem
 * depender de ninguém digitar nada.
 */
export interface FleetAgent {
  id: number;
  equipamentId: number;
  osUser: string;
  /** UPN cru reportado pela máquina — existe mesmo quando não casa com colaborador nenhum. */
  userUpn: string | null;
  employeeId: number | null;
  /** Null = dono não identificado (máquina fora do domínio, ou pessoa fora do cadastro). */
  employeeName: string | null;
  prefix: string;
  enrolledAt: string;
  lastSeenAt: string | null;
  widgetVersion: string | null;
  revokedAt: string | null;
  revokedBy: string | null;
  revokedReason: string | null;
}

/**
 * Um período de uso da máquina.
 *
 * ⚠ `durationMinutes` é **estimativa por baixo**: o fim da sessão não é capturado (desligamento
 * forçado não avisa ninguém), então a conta é "último batimento − logon" e erra em até um ciclo de
 * reporte. É melhor que uma sessão de três dias porque alguém segurou o botão.
 */
export interface FleetSession {
  id: number;
  equipamentId: number;
  osUser: string;
  userUpn: string | null;
  employeeId: number | null;
  employeeName: string | null;
  logonAt: string;
  bootAt: string | null;
  lastHeartbeatAt: string;
  durationMinutes: number;
  startCity: string | null;
  lastCity: string | null;
  lastRegion: string | null;
  lastLat: number | null;
  lastLon: number | null;
}

export interface Equipament {
  id: number;
  serieNumber: string;
  model: string;
  year: number;
  purchaseDate: string;
  processor: string;
  memory: string;
  storage: string;
  status: EquipamentStatus;
  actualUser: string | null;

  /** Quem está com o equipamento, como vínculo. Null = dono não identificado. */
  employeeId?: number | null;

  /**
   * Posse fixada à mão pela TI: nenhuma automação troca este dono. Sem isso, a regra de
   * predominância de uso (7 dias) pode reatribuir a máquina sozinha.
   */
  ownerPinned?: boolean;

  /** Identidade da máquina (UUID → serial → MAC → GUID do agente). Substituiu o serial como chave. */
  machineFingerprint?: string | null;

  initialDate: string | null;
  payerClientId: number | null;
  monthlyValue: number | null;

  /**
   * Telemetria reportada pelo próprio widget a cada login (`POST /Equipament/report`).
   * `widgetVersion` nulo = a máquina ainda não reportou — não é o mesmo que "sem widget".
   */
  widgetVersion?: string | null;
  lastSeenAt?: string | null;

  /** Em que pé está o auto-update: `ok` · `checking` · `downloading` · `downloaded` · `error` · `dev`. */
  widgetUpdateStatus?: string | null;
  /** Última mensagem do updater, crua — é o que se leva para um chamado. */
  widgetUpdateError?: string | null;

  history?: EquipamentHistory[];
}

export interface EquipamentFormData {
  serieNumber: string;
  model: string;
  year: number;
  purchaseDate: string;
  processor: string;
  memory: string;
  storage: string;
  status: EquipamentStatus;
  actualUser?: string;
  initialDate?: string;
  payerClientId?: number | null;
  monthlyValue?: number | null;
}

export const statusLabels: Record<EquipamentStatus, string> = {
  em_uso: "Em Uso",
  disponivel: "Disponível",
  danificado: "Danificado",
};

export const statusColors: Record<EquipamentStatus, string> = {
  em_uso: "bg-blue-500",
  disponivel: "bg-green-500",
  danificado: "bg-red-500",
};
